import { useEffect, useMemo, useRef } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { useTranslation } from 'react-i18next'

import { useTheme } from '../hooks/useTheme'
import { useSyncHtmlLang } from '../hooks/useSyncHtmlLang'
import MenuBar from '../components/MenuBar/MenuBar'
import SiteFooter from '../components/SiteFooter/SiteFooter'
import { getFaqContent } from '../faq/index'
import styles from './FaqPage.module.css'

/** All languages for which a FAQ translation exists (mirrors faq/index.ts). */
const FAQ_LANGS = ['en', 'de', 'ja'] as const

export default function FaqPage() {
  const { lang: langParam } = useParams<{ lang?: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  // Resolve effective language: URL param → current i18n language → English
  const lang = (langParam && FAQ_LANGS.includes(langParam as typeof FAQ_LANGS[number]))
    ? langParam
    : FAQ_LANGS.includes(i18n.language as typeof FAQ_LANGS[number])
      ? i18n.language
      : 'en'

  useTheme()
  useSyncHtmlLang(lang)

  const entries = useMemo(() => getFaqContent(lang), [lang])
  const faqListRef = useRef<HTMLDivElement>(null)

  // Auto-open and scroll to the hash-targeted question on load / hash change.
  useEffect(() => {
    const hash = location.hash.slice(1) // strip leading '#'
    if (!hash) return
    const el = document.getElementById(hash)
    if (el instanceof HTMLDetailsElement) {
      el.open = true
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [location.hash])

  const expandAll = () => {
    const details = faqListRef.current?.querySelectorAll('details')
    details?.forEach(detail => { detail.open = true })
  }

  const collapseAll = () => {
    const details = faqListRef.current?.querySelectorAll('details')
    details?.forEach(detail => { detail.open = false })
  }

  return (
    <div className={styles.pageWrapper}>
      <MenuBar lang={lang} city="" />
      <main className={styles.main}>
        <div className="staticPage">
          <h1 className="staticPageTitle">{t('faq_title')}</h1>

          {/* ── Language switcher ─────────────────────────────────────── */}
          <div className={styles.langSwitcher} role="group" aria-label="FAQ language">
            {FAQ_LANGS.map(code => (
              <button
                key={code}
                type="button"
                className={`${styles.langBtn} ${code === lang ? styles.langBtnActive : ''}`}
                onClick={() => navigate(`/faq/${code}${location.hash}`)}
                aria-current={code === lang ? 'true' : undefined}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>

          <div className={styles.faqControls}>
            <button className={styles.faqControlButton} onClick={expandAll}>{t('expand_all')}</button>
            <button className={styles.faqControlButton} onClick={collapseAll}>{t('collapse_all')}</button>
          </div>
          <div className={styles.faqList} ref={faqListRef}>
            {entries.map(entry => {
              const answerHtml = DOMPurify.sanitize(marked.parse(entry.answer) as string)
              return (
                <details key={entry.id} id={entry.id} className={styles.faqItem}>
                  <summary className={styles.faqQuestion}>{entry.question}</summary>
                  <div
                    className={styles.faqAnswer}
                    dangerouslySetInnerHTML={{ __html: answerHtml }}
                  />
                </details>
              )
            })}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  )
}
